import { useMutation } from '@apollo/client/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { DELETE_MULTICAST_MAPPING } from '@optools/services/operator-gateway/btc/mutations';
import {
  PipelineMulticastMapping,
  SearchMulticastMappingQuery,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Trash } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export interface DeleteMulticastMappingProps {
  multicastMapping: PipelineMulticastMapping;
}

export function DeleteMulticastMapping({ multicastMapping }: DeleteMulticastMappingProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { callSign, id, multicastAddress, priority, region, sourceLocation } = multicastMapping;

  const [deleteMulticastMapping, { loading }] = useMutation(DELETE_MULTICAST_MAPPING, {
    onCompleted: (data) => {
      if (!data?.deleteMulticastMapping) {
        toast.error('Delete Multicast Mapping', {
          description: `Failed to delete multicast mapping with id: ${id}`,
          duration: Infinity,
        });
        return;
      }

      toast.success('Delete Multicast Mapping', {
        description: `Successfully deleted multicast mapping with id: ${id}`,
      });
      setIsOpen(false);
    },
    update: (cache, { data }) => {
      if (!data?.deleteMulticastMapping) return;

      cache.modify<SearchMulticastMappingQuery>({
        fields: {
          searchMulticastMappings(existing, { readField }) {
            return existing.filter((item) => id !== readField('id', item));
          },
        },
      });
    },
  });

  const onDelete = () => deleteMulticastMapping({ variables: { id, region } });

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button aria-label="delete multicast mapping" size="icon" variant="destructive">
          <Trash />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogTitle>Delete Multicast Mapping</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you wish to delete the following multicast mapping?
        </AlertDialogDescription>
        <Card className="flex flex-wrap gap-4 p-4 text-sm">
          <dl className="flex gap-1">
            <dt className="font-semibold">ID:</dt>
            <dd>{id}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Call Sign:</dt>
            <dd>{callSign}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Priority:</dt>
            <dd>{priority}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Region:</dt>
            <dd>{region}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Source Location:</dt>
            <dd>{sourceLocation}</dd>
          </dl>
          <dl className="flex gap-1">
            <dt className="font-semibold">Multicast Address:</dt>
            <dd>{multicastAddress}</dd>
          </dl>
        </Card>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button disabled={loading} loading={loading} variant="destructive" onClick={onDelete}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
